import React, { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../auth'
import { useLang } from '../i18n'

// ---- Types ----

interface Member {
  id: string
  display_name: string
  username: string | null
  email: string
  role: 'member' | 'manager' | 'admin'
  region_code: string
  group_name: string
  is_group_lead: boolean
  account_status: 'active' | 'pending' | 'locked' | 'deactivated'
  is_active: boolean
}

interface Invitation {
  id: string
  email: string
  display_name: string
  region_code: string
  group_name: string
  role: string
  expires_at: string
}

// ---- Modal types ----

type ModalState =
  | { type: 'invite' }
  | { type: 'transfer'; member: Member }
  | { type: 'batchTransfer'; memberIds: string[] }
  | { type: 'setLead'; member: Member; existingLead: Member | null }
  | { type: 'deactivate'; member: Member }
  | { type: 'cancel'; member: Member }
  | { type: 'reactivate'; member: Member }

// ---- Shared styles ----

const inputStyle: React.CSSProperties = {
  padding: '6px 8px',
  border: '1px solid var(--border)',
  borderRadius: '4px',
  fontSize: '13px',
  width: '100%',
  boxSizing: 'border-box',
  background: 'var(--bg-surface)',
  color: 'var(--text-primary)',
}

const selectStyle: React.CSSProperties = {
  ...inputStyle,
  background: 'var(--bg-surface)',
}

const btnBase: React.CSSProperties = {
  padding: '4px 10px',
  border: '1px solid var(--border)',
  borderRadius: '4px',
  fontSize: '12px',
  cursor: 'pointer',
  background: 'var(--bg-surface)',
}

const btnPrimary: React.CSSProperties = {
  ...btnBase,
  background: 'var(--accent)',
  borderColor: 'var(--accent)',
  color: 'var(--accent-contrast)',
}

const btnDanger: React.CSSProperties = {
  ...btnBase,
  background: 'var(--danger)',
  borderColor: 'var(--danger)',
  color: 'var(--accent-contrast)',
}

const btnWarn: React.CSSProperties = {
  ...btnBase,
  background: 'var(--warning)',
  borderColor: 'var(--warning)',
  color: 'var(--accent-contrast)',
}

const tdStyle: React.CSSProperties = {
  padding: '8px 10px',
  borderBottom: '1px solid var(--border)',
  fontSize: '13px',
  verticalAlign: 'middle',
}

const thStyle: React.CSSProperties = {
  ...tdStyle,
  background: 'var(--bg-page)',
  fontWeight: 600,
  fontSize: '12px',
  color: 'var(--text-secondary)',
  whiteSpace: 'nowrap',
}

// ---- Status badge ----

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, { bg: string; color: string; label: string }> = {
    active:      { bg: 'var(--success-bg)', color: 'var(--success)', label: '正常' },
    pending:     { bg: 'var(--warning-bg)', color: 'var(--warning)', label: '待激活' },
    locked:      { bg: 'var(--warning-bg)', color: 'var(--warning)', label: '已锁定' },
    deactivated: { bg: 'var(--bg-elevated)', color: 'var(--text-muted)', label: '已停用' },
  }
  const cfg = colors[status] ?? { bg: 'var(--bg-elevated)', color: 'var(--text-muted)', label: status }
  return (
    <span style={{
      padding: '2px 7px',
      borderRadius: '10px',
      fontSize: '11px',
      background: cfg.bg,
      color: cfg.color,
      whiteSpace: 'nowrap',
    }}>
      {cfg.label}
    </span>
  )
}

// ---- Overlay modal wrapper ----

function Modal({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'var(--overlay)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{
        background: 'var(--bg-surface)', borderRadius: '8px', padding: '24px',
        maxWidth: '460px', width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
      }}>
        {children}
      </div>
    </div>
  )
}

// ---- Invite modal ----

function InviteModal({
  accessToken, onClose, onSuccess, t,
}: {
  accessToken: string
  onClose: () => void
  onSuccess: () => void
  t: (k: string) => string
}) {
  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [regionCode, setRegionCode] = useState('CN')
  const [groupName, setGroupName] = useState('CN-A')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSubmitting(true)
    try {
      const r = await fetch('/api/invitations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ email: email.trim(), display_name: displayName.trim(), region_code: regionCode, group_name: groupName, role: 'member' }),
      })
      if (!r.ok) {
        const body = await r.json().catch(() => ({}))
        setError(body?.error?.message ?? t('errorGeneric'))
        setSubmitting(false)
        return
      }
      onSuccess()
      onClose()
    } catch {
      setError(t('errorGeneric'))
      setSubmitting(false)
    }
  }

  return (
    <Modal onClose={onClose}>
      <h3 style={{ margin: '0 0 16px', fontSize: '16px' }}>{t('personnelInvite')}</h3>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{t('email')}</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required style={inputStyle} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{t('displayName')}</label>
          <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} required style={inputStyle} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{t('region')}</label>
          <select value={regionCode} onChange={(e) => setRegionCode(e.target.value)} style={selectStyle}>
            <option value="CN">CN</option>
            <option value="US">US</option>
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{t('group')}</label>
          <select value={groupName} onChange={(e) => setGroupName(e.target.value)} style={selectStyle}>
            <option value="CN-A">CN-A</option>
            <option value="CN-B">CN-B</option>
            <option value="US">US</option>
          </select>
        </div>
        {error && <div style={{ color: 'var(--danger)', fontSize: '12px' }}>{error}</div>}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '4px' }}>
          <button type="button" style={btnBase} onClick={onClose}>{t('personnelCancel')}</button>
          <button type="submit" style={btnPrimary} disabled={submitting}>{submitting ? t('loading') : t('personnelSubmit')}</button>
        </div>
      </form>
    </Modal>
  )
}

// ---- Transfer modal ----

function TransferModal({
  member, accessToken, onClose, onSuccess, t,
}: {
  member: Member
  accessToken: string
  onClose: () => void
  onSuccess: () => void
  t: (k: string) => string
}) {
  const [groupName, setGroupName] = useState(member.group_name)
  const [regionCode, setRegionCode] = useState(member.region_code)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSubmitting(true)
    try {
      const r = await fetch(`/api/members/${member.id}/transfer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ group_name: groupName, region_code: regionCode }),
      })
      if (!r.ok) {
        const body = await r.json().catch(() => ({}))
        setError(body?.error?.message ?? t('errorGeneric'))
        setSubmitting(false)
        return
      }
      onSuccess()
      onClose()
    } catch {
      setError(t('errorGeneric'))
      setSubmitting(false)
    }
  }

  return (
    <Modal onClose={onClose}>
      <h3 style={{ margin: '0 0 16px', fontSize: '16px' }}>{t('personnelTransfer')}: {member.display_name}</h3>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{t('group')}</label>
          <select value={groupName} onChange={(e) => setGroupName(e.target.value)} style={selectStyle}>
            <option value="CN-A">CN-A</option>
            <option value="CN-B">CN-B</option>
            <option value="US">US</option>
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{t('region')}</label>
          <select value={regionCode} onChange={(e) => setRegionCode(e.target.value)} style={selectStyle}>
            <option value="CN">CN</option>
            <option value="US">US</option>
          </select>
        </div>
        {error && <div style={{ color: 'var(--danger)', fontSize: '12px' }}>{error}</div>}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '4px' }}>
          <button type="button" style={btnBase} onClick={onClose}>{t('personnelCancel')}</button>
          <button type="submit" style={btnPrimary} disabled={submitting}>{submitting ? t('loading') : t('personnelConfirm')}</button>
        </div>
      </form>
    </Modal>
  )
}

// ---- Batch transfer modal ----

function BatchTransferModal({
  memberIds, accessToken, onClose, onSuccess, t,
}: {
  memberIds: string[]
  accessToken: string
  onClose: () => void
  onSuccess: () => void
  t: (k: string) => string
}) {
  const [groupName, setGroupName] = useState('CN-A')
  const [regionCode, setRegionCode] = useState('CN')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSubmitting(true)
    try {
      const r = await fetch('/api/members/batch-transfer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ member_ids: memberIds, group_name: groupName, region_code: regionCode }),
      })
      if (!r.ok) {
        const body = await r.json().catch(() => ({}))
        setError(body?.error?.message ?? t('errorGeneric'))
        setSubmitting(false)
        return
      }
      onSuccess()
      onClose()
    } catch {
      setError(t('errorGeneric'))
      setSubmitting(false)
    }
  }

  return (
    <Modal onClose={onClose}>
      <h3 style={{ margin: '0 0 8px', fontSize: '16px' }}>{t('personnelBatchTransfer')}</h3>
      <p style={{ margin: '0 0 14px', fontSize: '13px', color: 'var(--text-secondary)' }}>
        {t('personnelBatchTransferCount').replace('{n}', String(memberIds.length))}
      </p>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{t('group')}</label>
          <select value={groupName} onChange={(e) => setGroupName(e.target.value)} style={selectStyle}>
            <option value="CN-A">CN-A</option>
            <option value="CN-B">CN-B</option>
            <option value="US">US</option>
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{t('region')}</label>
          <select value={regionCode} onChange={(e) => setRegionCode(e.target.value)} style={selectStyle}>
            <option value="CN">CN</option>
            <option value="US">US</option>
          </select>
        </div>
        {error && <div style={{ color: 'var(--danger)', fontSize: '12px' }}>{error}</div>}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '4px' }}>
          <button type="button" style={btnBase} onClick={onClose}>{t('personnelCancel')}</button>
          <button type="submit" style={btnPrimary} disabled={submitting}>{submitting ? t('loading') : t('personnelConfirm')}</button>
        </div>
      </form>
    </Modal>
  )
}

// ---- Set lead confirmation modal ----

function SetLeadModal({
  member, existingLead, accessToken, onClose, onSuccess, t,
}: {
  member: Member
  existingLead: Member | null
  accessToken: string
  onClose: () => void
  onSuccess: () => void
  t: (k: string) => string
}) {
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const isSettingLead = !member.is_group_lead

  const handleConfirm = async () => {
    setError('')
    setSubmitting(true)
    try {
      const r = await fetch(`/api/members/${member.id}/set-lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ is_group_lead: isSettingLead, force: true }),
      })
      if (!r.ok) {
        const body = await r.json().catch(() => ({}))
        setError(body?.error?.message ?? t('errorGeneric'))
        setSubmitting(false)
        return
      }
      onSuccess()
      onClose()
    } catch {
      setError(t('errorGeneric'))
      setSubmitting(false)
    }
  }

  return (
    <Modal onClose={onClose}>
      <h3 style={{ margin: '0 0 12px', fontSize: '16px' }}>
        {isSettingLead ? t('personnelSetLead') : t('personnelUnsetLead')}: {member.display_name}
      </h3>
      {isSettingLead && existingLead && (
        <div style={{
          padding: '10px 12px', marginBottom: '14px',
          background: 'var(--warning-bg)', border: '1px solid var(--warning)',
          borderRadius: '4px', fontSize: '13px', color: 'var(--warning)',
        }}>
          {t('personnelSetLeadWarning').replace('{name}', existingLead.display_name)}
        </div>
      )}
      {error && <div style={{ color: 'var(--danger)', fontSize: '12px', marginBottom: '10px' }}>{error}</div>}
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
        <button style={btnBase} onClick={onClose}>{t('personnelCancel')}</button>
        <button style={btnPrimary} disabled={submitting} onClick={handleConfirm}>
          {submitting ? t('loading') : t('personnelConfirm')}
        </button>
      </div>
    </Modal>
  )
}

// ---- Deactivate confirmation modal ----

function DeactivateModal({
  member, accessToken, onClose, onSuccess, t,
}: {
  member: Member
  accessToken: string
  onClose: () => void
  onSuccess: () => void
  t: (k: string) => string
}) {
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const handleConfirm = async () => {
    setError('')
    setSubmitting(true)
    try {
      const r = await fetch(`/api/members/${member.id}/deactivate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      if (!r.ok) {
        const body = await r.json().catch(() => ({}))
        setError(body?.error?.message ?? t('errorGeneric'))
        setSubmitting(false)
        return
      }
      onSuccess()
      onClose()
    } catch {
      setError(t('errorGeneric'))
      setSubmitting(false)
    }
  }

  return (
    <Modal onClose={onClose}>
      <h3 style={{ margin: '0 0 12px', fontSize: '16px' }}>{t('personnelDeactivate')}: {member.display_name}</h3>
      <p style={{ margin: '0 0 14px', fontSize: '13px', color: 'var(--text-secondary)' }}>{t('personnelDeactivateConfirm')}</p>
      {error && <div style={{ color: 'var(--danger)', fontSize: '12px', marginBottom: '10px' }}>{error}</div>}
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
        <button style={btnBase} onClick={onClose}>{t('personnelCancel')}</button>
        <button style={btnWarn} disabled={submitting} onClick={handleConfirm}>
          {submitting ? t('loading') : t('personnelConfirm')}
        </button>
      </div>
    </Modal>
  )
}

// ---- Cancel (注销) modal ----

function CancelModal({
  member, accessToken, onClose, onSuccess, t,
}: {
  member: Member
  accessToken: string
  onClose: () => void
  onSuccess: () => void
  t: (k: string) => string
}) {
  const [nameConfirm, setNameConfirm] = useState('')
  const [reason, setReason] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const canSubmit = nameConfirm === member.display_name && reason.trim().length > 0

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    setError('')
    setSubmitting(true)
    try {
      const r = await fetch(`/api/members/${member.id}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ display_name_confirm: nameConfirm, reason: reason.trim() }),
      })
      if (!r.ok) {
        const body = await r.json().catch(() => ({}))
        setError(body?.error?.message ?? t('errorGeneric'))
        setSubmitting(false)
        return
      }
      onSuccess()
      onClose()
    } catch {
      setError(t('errorGeneric'))
      setSubmitting(false)
    }
  }

  return (
    <Modal onClose={onClose}>
      <h3 style={{ margin: '0 0 8px', fontSize: '16px' }}>{t('personnelCancelAccount')}: {member.display_name}</h3>
      <div style={{
        padding: '10px 12px', marginBottom: '14px',
        background: 'var(--danger-bg)', border: '1px solid var(--danger)',
        borderRadius: '4px', fontSize: '13px', color: 'var(--danger)',
      }}>
        {t('personnelCancelWarning')}
      </div>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
            {t('personnelCancelNameConfirm').replace('{name}', member.display_name)}
          </label>
          <input
            type="text" value={nameConfirm}
            onChange={(e) => setNameConfirm(e.target.value)}
            style={inputStyle} autoFocus
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{t('personnelCancelReason')}</label>
          <textarea
            value={reason} onChange={(e) => setReason(e.target.value)} required
            rows={3}
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        </div>
        {error && <div style={{ color: 'var(--danger)', fontSize: '12px' }}>{error}</div>}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button type="button" style={btnBase} onClick={onClose}>{t('personnelCancel')}</button>
          <button
            type="submit"
            style={{ ...btnDanger, opacity: canSubmit ? 1 : 0.4, cursor: canSubmit ? 'pointer' : 'not-allowed' }}
            disabled={!canSubmit || submitting}
          >
            {submitting ? t('loading') : t('personnelCancelConfirm')}
          </button>
        </div>
      </form>
    </Modal>
  )
}

// ---- Reactivate confirmation modal ----

function ReactivateModal({
  member, accessToken, onClose, onSuccess, t,
}: {
  member: Member
  accessToken: string
  onClose: () => void
  onSuccess: () => void
  t: (k: string) => string
}) {
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const handleConfirm = async () => {
    setError('')
    setSubmitting(true)
    try {
      const r = await fetch(`/api/members/${member.id}/reactivate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      if (!r.ok) {
        const body = await r.json().catch(() => ({}))
        setError(body?.error?.message ?? t('errorGeneric'))
        setSubmitting(false)
        return
      }
      onSuccess()
      onClose()
    } catch {
      setError(t('errorGeneric'))
      setSubmitting(false)
    }
  }

  return (
    <Modal onClose={onClose}>
      <h3 style={{ margin: '0 0 12px', fontSize: '16px' }}>{t('personnelReactivate')}: {member.display_name}</h3>
      <p style={{ margin: '0 0 14px', fontSize: '13px', color: 'var(--text-secondary)' }}>{t('personnelReactivateConfirm')}</p>
      {error && <div style={{ color: 'var(--danger)', fontSize: '12px', marginBottom: '10px' }}>{error}</div>}
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
        <button style={btnBase} onClick={onClose}>{t('personnelCancel')}</button>
        <button style={btnPrimary} disabled={submitting} onClick={handleConfirm}>
          {submitting ? t('loading') : t('personnelConfirm')}
        </button>
      </div>
    </Modal>
  )
}

// ---- Reusable panel (used in both PersonnelPage and AdminConsolePage) ----

export function PersonnelManagementPanel() {
  const { t } = useLang()
  const { user, accessToken } = useAuth()

  const canManage = user?.role === 'manager' || user?.role === 'admin'

  const [members, setMembers] = useState<Member[]>([])
  const [invitations, setInvitations] = useState<Invitation[]>([])
  const [loading, setLoading] = useState(false)
  const [invLoading, setInvLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showDeactivated, setShowDeactivated] = useState(false)
  const [modal, setModal] = useState<ModalState | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [actionError, setActionError] = useState<string | null>(null)
  const [actionLoading, setActionLoading] = useState<string | null>(null) // memberId or invitationId

  const fetchMembers = useCallback(async () => {
    if (!accessToken) return
    setLoading(true)
    setError(null)
    try {
      const qs = showDeactivated ? '?include_deactivated=true' : ''
      const r = await fetch(`/api/members${qs}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      if (!r.ok) {
        const body = await r.json().catch(() => ({}))
        setError(body?.error?.message ?? t('errorGeneric'))
        return
      }
      const data = await r.json()
      setMembers(data.members ?? [])
    } catch {
      setError(t('errorGeneric'))
    } finally {
      setLoading(false)
    }
  }, [accessToken, showDeactivated, t])

  const fetchInvitations = useCallback(async () => {
    if (!accessToken || !canManage) return
    setInvLoading(true)
    try {
      const r = await fetch('/api/invitations', {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      if (r.ok) {
        const data = await r.json()
        setInvitations(data.invitations ?? [])
      }
    } catch { /* ignore */ } finally {
      setInvLoading(false)
    }
  }, [accessToken, canManage])

  useEffect(() => {
    fetchMembers()
  }, [fetchMembers])

  useEffect(() => {
    fetchInvitations()
  }, [fetchInvitations])

  const refreshAll = useCallback(() => {
    fetchMembers()
    fetchInvitations()
  }, [fetchMembers, fetchInvitations])

  // ---- Inline action handlers ----

  async function handleUnlock(member: Member) {
    if (!accessToken) return
    setActionLoading(member.id)
    setActionError(null)
    try {
      const r = await fetch(`/api/members/${member.id}/unlock`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      if (!r.ok) {
        const body = await r.json().catch(() => ({}))
        setActionError(body?.error?.message ?? t('errorGeneric'))
      } else {
        await fetchMembers()
      }
    } catch {
      setActionError(t('errorGeneric'))
    } finally {
      setActionLoading(null)
    }
  }

  async function handleResendInvitation(inv: Invitation) {
    if (!accessToken) return
    setActionLoading(inv.id)
    setActionError(null)
    try {
      const r = await fetch(`/api/invitations/${inv.id}/resend`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      if (!r.ok) {
        const body = await r.json().catch(() => ({}))
        setActionError(body?.error?.message ?? t('errorGeneric'))
      }
    } catch {
      setActionError(t('errorGeneric'))
    } finally {
      setActionLoading(null)
    }
  }

  async function handleResendInviteForMember(member: Member) {
    if (!accessToken) return
    setActionLoading(member.id)
    setActionError(null)
    // Find invitation by email
    const inv = invitations.find((i) => i.email === member.email)
    if (!inv) {
      setActionError(t('errorGeneric'))
      setActionLoading(null)
      return
    }
    try {
      const r = await fetch(`/api/invitations/${inv.id}/resend`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      if (!r.ok) {
        const body = await r.json().catch(() => ({}))
        setActionError(body?.error?.message ?? t('errorGeneric'))
      }
    } catch {
      setActionError(t('errorGeneric'))
    } finally {
      setActionLoading(null)
    }
  }

  async function handleRevokeInvitation(inv: Invitation) {
    if (!accessToken) return
    setActionLoading(inv.id)
    setActionError(null)
    try {
      const r = await fetch(`/api/invitations/${inv.id}/revoke`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      if (!r.ok) {
        const body = await r.json().catch(() => ({}))
        setActionError(body?.error?.message ?? t('errorGeneric'))
      } else {
        await fetchInvitations()
      }
    } catch {
      setActionError(t('errorGeneric'))
    } finally {
      setActionLoading(null)
    }
  }

  // ---- Selection ----

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleSelectAll() {
    const activeMembers = members.filter((m) => m.account_status !== 'deactivated' || showDeactivated)
    if (selectedIds.size === activeMembers.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(activeMembers.map((m) => m.id)))
    }
  }

  // ---- Set lead helper ----

  function openSetLead(member: Member) {
    const willSet = !member.is_group_lead
    const existingLead = willSet
      ? members.find((m) => m.is_group_lead && m.group_name === member.group_name && m.id !== member.id) ?? null
      : null
    setModal({ type: 'setLead', member, existingLead })
  }

  // ---- Visible members (respect showDeactivated toggle) ----

  const visibleMembers = showDeactivated
    ? members
    : members.filter((m) => m.account_status !== 'deactivated' && m.is_active)

  // ---- Render ----

  return (
    <div style={{ maxWidth: '1400px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>{t('navPersonnel')}</h2>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'var(--text-secondary)', cursor: 'pointer', marginLeft: '8px' }}>
          <input
            type="checkbox"
            checked={showDeactivated}
            onChange={(e) => setShowDeactivated(e.target.checked)}
          />
          {t('personnelShowDeactivated')}
        </label>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>
          {canManage && selectedIds.size > 0 && (
            <button
              style={btnPrimary}
              onClick={() => setModal({ type: 'batchTransfer', memberIds: Array.from(selectedIds) })}
            >
              {t('personnelBatchTransfer')} ({selectedIds.size})
            </button>
          )}
          {canManage && (
            <button style={btnPrimary} onClick={() => setModal({ type: 'invite' })}>
              + {t('personnelInvite')}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div style={{ padding: '10px 12px', marginBottom: '14px', background: 'var(--danger-bg)', border: '1px solid var(--danger)', borderRadius: '4px', color: 'var(--danger)', fontSize: '13px' }}>
          {error}
        </div>
      )}
      {actionError && (
        <div style={{ padding: '10px 12px', marginBottom: '14px', background: 'var(--danger-bg)', border: '1px solid var(--danger)', borderRadius: '4px', color: 'var(--danger)', fontSize: '13px' }}>
          {actionError}
          <button style={{ ...btnBase, marginLeft: '10px', fontSize: '11px', padding: '2px 6px' }} onClick={() => setActionError(null)}>✕</button>
        </div>
      )}

      {/* Member table */}
      {loading ? (
        <p style={{ color: 'var(--text-muted)' }}>{t('loading')}</p>
      ) : (
        <div style={{ border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflowX: 'auto', marginBottom: '32px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
            <thead>
              <tr>
                {canManage && (
                  <th style={{ ...thStyle, width: '36px', textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={selectedIds.size > 0 && selectedIds.size === visibleMembers.length}
                      onChange={toggleSelectAll}
                    />
                  </th>
                )}
                <th style={thStyle}>{t('displayName')}</th>
                <th style={thStyle}>{t('username')}</th>
                <th style={thStyle}>{t('email')}</th>
                <th style={thStyle}>{t('role')}</th>
                <th style={thStyle}>{t('region')}</th>
                <th style={thStyle}>{t('group')}</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>{t('personnelGroupLead')}</th>
                <th style={thStyle}>{t('personnelStatus')}</th>
                {canManage && <th style={{ ...thStyle, minWidth: '200px' }}>{t('personnelActions')}</th>}
              </tr>
            </thead>
            <tbody>
              {visibleMembers.length === 0 && (
                <tr>
                  <td colSpan={canManage ? 10 : 8} style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>—</td>
                </tr>
              )}
              {visibleMembers.map((member) => {
                const isSelf = user?.id === member.id
                const actuallyDeactivated = !member.is_active || member.account_status === 'deactivated'
                return (
                  <tr key={member.id} style={{ background: actuallyDeactivated ? 'var(--bg-page)' : undefined, opacity: actuallyDeactivated ? 0.65 : 1 }}>
                    {canManage && (
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(member.id)}
                          onChange={() => toggleSelect(member.id)}
                        />
                      </td>
                    )}
                    <td style={{ ...tdStyle, fontWeight: 500 }}>{member.display_name}</td>
                    <td style={{ ...tdStyle, color: 'var(--text-secondary)' }}>{member.username ?? '—'}</td>
                    <td style={{ ...tdStyle, color: 'var(--text-secondary)' }}>{member.email}</td>
                    <td style={tdStyle}>
                      <span style={{
                        padding: '2px 6px', borderRadius: '3px', fontSize: '11px',
                        background: member.role === 'admin' ? 'var(--warning-bg)' : member.role === 'manager' ? 'var(--info-bg)' : 'var(--bg-elevated)',
                        color: member.role === 'admin' ? 'var(--warning)' : member.role === 'manager' ? 'var(--info)' : 'var(--text-secondary)',
                      }}>
                        {member.role}
                      </span>
                    </td>
                    <td style={tdStyle}>{member.region_code}</td>
                    <td style={tdStyle}>{member.group_name}</td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      {member.is_group_lead ? '🎖' : ''}
                    </td>
                    <td style={tdStyle}><StatusBadge status={member.account_status} /></td>
                    {canManage && (
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
                          {/* Locked: unlock */}
                          {member.account_status === 'locked' && (
                            <button
                              style={btnPrimary}
                              disabled={actionLoading === member.id}
                              onClick={() => handleUnlock(member)}
                            >
                              {t('personnelUnlock')}
                            </button>
                          )}
                          {/* Pending: resend invitation */}
                          {member.account_status === 'pending' && (
                            <button
                              style={btnBase}
                              disabled={actionLoading === member.id}
                              onClick={() => handleResendInviteForMember(member)}
                            >
                              {t('personnelResendInvite')}
                            </button>
                          )}
                          {/* Active members: set lead, transfer, deactivate */}
                          {member.account_status === 'active' && member.is_active && (
                            <>
                              <button
                                style={btnBase}
                                disabled={actionLoading === member.id}
                                onClick={() => openSetLead(member)}
                              >
                                {member.is_group_lead ? t('personnelUnsetLead') : t('personnelSetLead')}
                              </button>
                              <button
                                style={btnBase}
                                disabled={actionLoading === member.id}
                                onClick={() => setModal({ type: 'transfer', member })}
                              >
                                {t('personnelTransfer')}
                              </button>
                              {!isSelf && (
                                <button
                                  style={{ ...btnBase, borderColor: 'var(--warning)', color: 'var(--warning)' }}
                                  disabled={actionLoading === member.id}
                                  onClick={() => setModal({ type: 'deactivate', member })}
                                >
                                  {t('personnelDeactivate')}
                                </button>
                              )}
                            </>
                          )}
                          {/* Deactivated (is_active=false, account_status=active): reactivate */}
                          {!member.is_active && member.account_status === 'active' && (
                            <button
                              style={btnPrimary}
                              disabled={actionLoading === member.id}
                              onClick={() => setModal({ type: 'reactivate', member })}
                            >
                              {t('personnelReactivate')}
                            </button>
                          )}
                          {/* Any non-self non-deactivated member: cancel */}
                          {!isSelf && member.account_status !== 'deactivated' && (
                            <button
                              style={btnDanger}
                              disabled={actionLoading === member.id}
                              onClick={() => setModal({ type: 'cancel', member })}
                            >
                              {t('personnelCancelAccount')}
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pending Invitations */}
      {canManage && (
        <div>
          <h3 style={{ fontSize: '15px', margin: '0 0 12px', color: 'var(--text-primary)' }}>{t('personnelPendingInvitations')}</h3>
          {invLoading ? (
            <p style={{ color: 'var(--text-muted)', fontSize: '13px' }}>{t('loading')}</p>
          ) : invitations.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: '13px' }}>—</p>
          ) : (
            <div style={{ border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden', overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>{t('email')}</th>
                    <th style={thStyle}>{t('displayName')}</th>
                    <th style={thStyle}>{t('region')}</th>
                    <th style={thStyle}>{t('group')}</th>
                    <th style={thStyle}>{t('role')}</th>
                    <th style={thStyle}>{t('personnelExpiresAt')}</th>
                    <th style={thStyle}>{t('personnelActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {invitations.map((inv) => (
                    <tr key={inv.id}>
                      <td style={tdStyle}>{inv.email}</td>
                      <td style={tdStyle}>{inv.display_name}</td>
                      <td style={tdStyle}>{inv.region_code}</td>
                      <td style={tdStyle}>{inv.group_name}</td>
                      <td style={tdStyle}>{inv.role}</td>
                      <td style={{ ...tdStyle, color: 'var(--text-muted)', fontSize: '12px' }}>
                        {new Date(inv.expires_at).toLocaleString()}
                      </td>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button
                            style={btnBase}
                            disabled={actionLoading === inv.id}
                            onClick={() => handleResendInvitation(inv)}
                          >
                            {t('personnelResend')}
                          </button>
                          <button
                            style={btnDanger}
                            disabled={actionLoading === inv.id}
                            onClick={() => handleRevokeInvitation(inv)}
                          >
                            {t('personnelRevoke')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      {modal?.type === 'invite' && accessToken && (
        <InviteModal
          accessToken={accessToken}
          onClose={() => setModal(null)}
          onSuccess={refreshAll}
          t={t}
        />
      )}
      {modal?.type === 'transfer' && accessToken && (
        <TransferModal
          member={modal.member}
          accessToken={accessToken}
          onClose={() => setModal(null)}
          onSuccess={() => { refreshAll(); setSelectedIds(new Set()) }}
          t={t}
        />
      )}
      {modal?.type === 'batchTransfer' && accessToken && (
        <BatchTransferModal
          memberIds={modal.memberIds}
          accessToken={accessToken}
          onClose={() => setModal(null)}
          onSuccess={() => { refreshAll(); setSelectedIds(new Set()) }}
          t={t}
        />
      )}
      {modal?.type === 'setLead' && accessToken && (
        <SetLeadModal
          member={modal.member}
          existingLead={modal.existingLead}
          accessToken={accessToken}
          onClose={() => setModal(null)}
          onSuccess={refreshAll}
          t={t}
        />
      )}
      {modal?.type === 'deactivate' && accessToken && (
        <DeactivateModal
          member={modal.member}
          accessToken={accessToken}
          onClose={() => setModal(null)}
          onSuccess={refreshAll}
          t={t}
        />
      )}
      {modal?.type === 'cancel' && accessToken && (
        <CancelModal
          member={modal.member}
          accessToken={accessToken}
          onClose={() => setModal(null)}
          onSuccess={refreshAll}
          t={t}
        />
      )}
      {modal?.type === 'reactivate' && accessToken && (
        <ReactivateModal
          member={modal.member}
          accessToken={accessToken}
          onClose={() => setModal(null)}
          onSuccess={refreshAll}
          t={t}
        />
      )}
    </div>
  )
}

// ---- Page wrapper (Manager's standalone /personnel route) ----

export function PersonnelPage() {
  return <PersonnelManagementPanel />
}
